import React, { createRef, FunctionComponent, KeyboardEventHandler, MouseEventHandler, useEffect } from "react";

import { BooleanSwitcher, BooleanSwitcherChildrenProps, Icon, IconColor } from "@multiweb/reactComponents";
import { bemClassNames, TAB_INDEX_FOCUSABLE_ONLY, useSetFocus } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { ActionItemHoverType } from "../../types";
import { activationKeyBlockHandler, activationMouseBlockHandler, createActivationKeyHandler, createActivationMouseHandler } from "../../utils";

export type ActionItemComponentProps = {
    /** Příznak, zda se má použít barva pozadí pro submenu. */
    hasSubmenuBackground?: boolean;
    /** Kód ikony */
    iconCode: IconCode;
    /** Příznak, zda má tlačítko vypadat jako po najetí myši */
    isActive?: boolean;
    /** Callback volaný při kliknutí. Reaguje na primární i ostatní tlačítka myši (přidává se na onClick i onAuxClick). */
    onClick?: MouseEventHandler<HTMLButtonElement>;
    /** Callback volaný při uvolnění klávesy */
    onKeyUp?: KeyboardEventHandler<HTMLButtonElement>;
    /** Popis pole zobrazený po najetí myši */
    title?: string;
    /** Typ tlačítka udává barvu po najetí myši */
    hoverType?: ActionItemHoverType;
    /** Příznak, zda je komponenta zaměřená. */
    isFocused: boolean;
    /** Vyšle akci indikující, že proběhlo zaměření položky. Viz dokumentace v menu/navigation. */
    actionItemFocused?: () => void;
};

const classNames = bemClassNames("action-item-component");

/** Tlačítko pro hover menu jednotlivých položek v menu */
export const ActionItemComponent: FunctionComponent<ActionItemComponentProps> = ({
    hasSubmenuBackground = false,
    iconCode,
    isActive = false,
    onClick,
    onKeyUp,
    title,
    hoverType = ActionItemHoverType.primary,
    isFocused,
    actionItemFocused,
}) => {
    const ref = createRef<HTMLButtonElement>();
    /* Vyšle akci kvůli opravě zaměření. Viz dokumentaci v menu/navigation. */
    useEffect(() => {
        if (isFocused && actionItemFocused) {
            actionItemFocused();
        }
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]);
    useSetFocus<HTMLButtonElement>(isFocused, ref);
    const handleClick = createActivationMouseHandler(onClick);
    const handleKeyUp = createActivationKeyHandler(onKeyUp);

    return (
        <BooleanSwitcher>
            {({ value: isHovered, turnOn: onMouseEnter, turnOff: onMouseLeave }: BooleanSwitcherChildrenProps) => (
                <div
                    className={classNames({ "submenu-background": hasSubmenuBackground, "clickable": !!onClick })}
                    onFocus={onMouseEnter}
                    onBlur={onMouseLeave}
                    onMouseMove={onMouseEnter}
                    {...{ onMouseEnter, onMouseLeave }}
                >
                    <div className={classNames("highlight", { "active": isActive })}>
                        <button
                            className={classNames(
                                "button",
                                { "type": hoverType, "hover": isHovered },
                            )}
                            onClick={handleClick}
                            onAuxClick={handleClick}
                            onMouseDown={activationMouseBlockHandler}
                            onKeyUp={handleKeyUp}
                            onKeyDown={activationKeyBlockHandler}
                            tabIndex={TAB_INDEX_FOCUSABLE_ONLY}
                            {...{ title, ref }}
                        >
                            <Icon
                                iconCode={iconCode}
                                color={(isHovered || isActive) ? IconColor.whiteFixed : IconColor.grey1}
                            />
                        </button>
                    </div>
                </div>
            )}
        </BooleanSwitcher>
    );
};
